import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { ShoppingBag, Heart, ArrowLeft } from 'lucide-react';
import { addToCart, getToken, removeToken, removeUser, addToWishlist, removeFromWishlist, getWishlist } from '../api/auth';
import { useCurrency } from '../context/CurrencyContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const BADGE_COLORS = {
  'New':        { bg: '#1C1A18', color: '#FAF8F4' },
  'Bestseller': { bg: '#4A3728', color: '#D4AA70' },
  'Sale':       { bg: '#C0392B', color: '#FAF8F4' },
  'Limited':    { bg: '#1C1A18', color: '#B08D57' },
  'Trending':   { bg: '#B08D57', color: '#FAF8F4' },
  'Hot':        { bg: '#C0392B', color: '#FAF8F4' },
  'Exclusive':  { bg: '#1C1A18', color: '#D4AA70' },
};

const getProductImages = (product) => {
  const fields = [product.image_1, product.image_2, product.image_3].filter(Boolean);
  const jsonImages = Array.isArray(product.images) ? product.images.filter(Boolean) : [];
  return [...new Set([...fields, ...jsonImages])];
};

const ImageSwiper = ({ product }) => {
  const imgs = getProductImages(product);
  const count = imgs.length;
  const [current, setCurrent] = useState(0);
  const touchStartX = useRef(null);

  const go = (dir, e) => {
    e.stopPropagation();
    setCurrent(c => (c + dir + count) % count);
  };

  const onTouchStart = (e) => { touchStartX.current = e.changedTouches[0].screenX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].screenX;
    if (Math.abs(diff) > 40) go(diff > 0 ? 1 : -1, e);
    touchStartX.current = null;
  };

  return (
    <div className="cd-slide-wrap" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <div className="cd-slide-strip" style={{ transform: `translateX(-${current * 100}%)` }}>
        {imgs.map((src, idx) => (
          <img key={idx} src={src} alt={`${product.name} ${idx + 1}`} loading="lazy" />
        ))}
      </div>
      {count > 1 && (
        <>
          <button className="cd-sarrow cd-sarrow-l" onClick={e => go(-1, e)} aria-label="Previous image">←</button>
          <button className="cd-sarrow cd-sarrow-r" onClick={e => go(1, e)} aria-label="Next image">→</button>
        </>
      )}
    </div>
  );
};

const Toast = ({ msg, show }) => (
  <AnimatePresence>
    {show && (
      <motion.div className="cd-toast"
        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 30 }} transition={{ duration: 0.3 }}>
        <ShoppingBag size={13} /> {msg}
      </motion.div>
    )}
  </AnimatePresence>
);

const ProductCard = ({ product, index, onAddToCart, collectionName, collectionSlug, wished, onToggleWishlist }) => {
  const ref    = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();

  const hasDiscount  = product.sale_price && product.sale_price < product.original_price;
  const displayPrice = hasDiscount ? product.sale_price : product.original_price;
  const mrpPrice     = hasDiscount ? product.original_price : null;
  const badge        = product.badge ? BADGE_COLORS[product.badge] : null;
  const discount     = mrpPrice ? Math.round((1 - displayPrice / mrpPrice) * 100) : 0;

  return (
    <motion.div ref={ref} className="cd-card"
      initial={{ opacity: 0, y: 32 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: (index % 4) * 0.08, ease: [0.25, 0.46, 0.45, 0.94] }}>

      <div className="cd-img-wrap"
        onClick={() => navigate(`/products/${product.id}`, {
          state: { collectionName, collectionSlug }
        })}
        style={{ cursor: 'pointer' }}>
        <ImageSwiper product={product} />
        <button className="cd-wish-btn"
          onClick={e => { e.stopPropagation(); onToggleWishlist(); }}
          aria-label={wished ? 'Remove from wishlist' : 'Add to wishlist'}>
          <Heart size={13} fill={wished ? '#C0392B' : 'none'} color={wished ? '#C0392B' : '#5C5247'} />
        </button>
        {badge && (
        <span className="cd-badge" style={{ background: badge.bg, color: badge.color }}>
          {product.badge}
        </span>
      )}
      {product.stock_qty === 0 && (
        <span style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          background: 'rgba(28,26,24,0.82)', color: '#fff',
          fontSize: 8, fontWeight: 700, letterSpacing: '0.18em',
          textTransform: 'uppercase', padding: '6px', textAlign: 'center', zIndex: 4,
        }}>Sold Out</span>
      )}
      </div>

      <div className="cd-info">
        <span className="cd-name"
          onClick={() => navigate(`/products/${product.id}`, {
            state: { collectionName, collectionSlug }
          })}
          style={{ cursor: 'pointer' }}>
          {product.name}
        </span>
        <div className="cd-price-row">
          <span className="cd-price">{formatPrice(displayPrice)}</span>
          {mrpPrice && <span className="cd-mrp">{formatPrice(mrpPrice)}</span>}
          {discount > 0 && <span className="cd-discount">{discount}% off</span>}
        </div>
        <button
        className="cd-atc"
        onClick={() => product.stock_qty !== 0 && onAddToCart(product)}
        disabled={product.stock_qty === 0}
        style={product.stock_qty === 0 ? { background: '#9A9186', cursor: 'not-allowed' } : {}}
      >
        <ShoppingBag size={12} />
        {product.stock_qty === 0 ? 'Sold Out'
          : ([...new Set(product.colors?.filter(c => c && c.trim()) ?? [])].length > 1 ||
          [...new Set(product.sizes?.filter(s => s && s.trim()) ?? [])].length > 1) ? 'Select Options'
          : 'Add to Cart'}
      </button>
      </div>
    </motion.div>
  );
};

const CollectionDetail = () => {
  const { slug } = useParams();

  const [meta, setMeta]       = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [wishlist, setWishlist] = useState(new Set());
  const navigate = useNavigate();
  const [toast, setToast]     = useState({ show: false, msg: '' });
  const [sortBy, setSortBy]   = useState('default');

  useEffect(() => {
    setLoading(true);
    setError('');

    if (getToken()) {
      getWishlist()
        .then(items => setWishlist(new Set(items.map(item => item.id))))
        .catch(() => {});
    }

    // Fetch collection meta + products in parallel
    Promise.all([
      fetch(`${API}/collections`).then(r => r.json()),
      fetch(`${API}/products?collection=${slug}&active=true`).then(r => r.json()),
    ])
      .then(([colData, prodData]) => {
        if (colData.success) {
          const found = colData.collections.find(c => c.slug === slug);
          setMeta(found || { name: slug, sub: '' });
        }
        if (prodData.success) setProducts(prodData.products);
        else setError(prodData.error || 'Failed to load products');
      })
      .catch(() => setError('Failed to connect to server'))
      .finally(() => setLoading(false));
  }, [slug]);

  const handleAddToCart = async (product) => {
  if (!getToken()) { navigate('/account'); return; }

  const colorCount = [...new Set(product.colors?.filter(c => c && c.trim()) ?? [])].length;
  const sizeCount  = [...new Set(product.sizes?.filter(s => s && s.trim()) ?? [])].length;

  if (colorCount > 1 || sizeCount > 1) {
    navigate(`/products/${product.id}`);
    return;
  }

  try {
    await addToCart(product.id, 1);
    setToast({ show: true, msg: `${product.name} added!` });
    setTimeout(() => setToast({ show: false, msg: '' }), 2200);
    window.dispatchEvent(new Event('cart-updated'));
  } catch (err) {
    const msg = err.message?.toLowerCase() || '';
    if (msg.includes('invalid') || msg.includes('expired') || msg.includes('session') || msg.includes('token') || msg.includes('unauthorized')) {
      removeToken(); removeUser(); navigate('/account'); return;
    }
    setToast({ show: true, msg: err.message || 'Failed to add to cart' });
    setTimeout(() => setToast({ show: false, msg: '' }), 2200);
  }
};

  const handleWishlist = async (productId) => {
    if (!getToken()) { navigate('/account'); return; }
    const isIn = wishlist.has(productId);
    setWishlist(prev => {
      const set = new Set(prev);
      isIn ? set.delete(productId) : set.add(productId);
      return set;
    });
    try {
      if (isIn) await removeFromWishlist(productId);
      else await addToWishlist(productId);
    } catch {
      setWishlist(prev => {
        const set = new Set(prev);
        isIn ? set.add(productId) : set.delete(productId);
        return set;
      });
    }
  };

  const sorted = [...products].sort((a, b) => {
    const aPrice = a.sale_price || a.original_price;
    const bPrice = b.sale_price || b.original_price;
    if (sortBy === 'price-asc')  return aPrice - bPrice;
    if (sortBy === 'price-desc') return bPrice - aPrice;
    if (sortBy === 'discount') {
      const aD = a.sale_price ? (1 - a.sale_price / a.original_price) : 0;
      const bD = b.sale_price ? (1 - b.sale_price / b.original_price) : 0;
      return bD - aD;
    }
    return (a.sort_order || 0) - (b.sort_order || 0);
  });

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=Jost:wght@300;400;500;600&display=swap');
        .cd-page { background: #FAF8F4; min-height: 100vh; padding-top: 100px; font-family: 'Jost', sans-serif; overflow-x: hidden; }
        .cd-header { padding: 36px clamp(20px,5vw,56px) 28px; border-top: 3px solid #B08D57; background: #FAF8F4; }
        .cd-header-back { display: inline-flex; align-items: center; gap: 6px; font-size: 9px; font-weight: 600; letter-spacing: 0.2em; text-transform: uppercase; color: #9A9186; text-decoration: none; margin-bottom: 20px; transition: color 0.2s; }
        .cd-header-back:hover { color: #B08D57; }
        .cd-header-eyebrow { font-size: 9px; font-weight: 500; letter-spacing: 0.3em; text-transform: uppercase; color: #B08D57; display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
        .cd-header-eyebrow::before { content:''; width:20px; height:1px; background:#B08D57; }
        .cd-header-title { font-family: 'Playfair Display', serif; font-size: clamp(30px, 4.5vw, 54px); font-weight: 700; color: #1C1A18; line-height: 1.05; letter-spacing: -0.02em; }
        .cd-header-rule { margin-top: 24px; height: 1px; background: linear-gradient(to right, #B08D57 0%, rgba(176,141,87,0.15) 60%, transparent 100%); }
        .cd-toolbar { position: relative; z-index: 1; display: flex; align-items: center; justify-content: space-between; padding: 14px clamp(20px,5vw,56px); background: #FAF8F4; border-bottom: 1px solid rgba(176,141,87,0.15); flex-wrap: wrap; gap: 10px; }
        .cd-toolbar-left { font-size: 11px; font-weight: 400; color: #8A7E72; }
        .cd-toolbar-left span { color: #1C1A18; font-weight: 500; }
        .cd-sort { display: flex; align-items: center; gap: 7px; font-size: 10px; font-weight: 500; letter-spacing: 0.12em; text-transform: uppercase; color: #5C5247; }
        .cd-sort select { font-family: 'Jost', sans-serif; font-size: 10px; color: #1C1A18; background: transparent; border: 1px solid rgba(176,141,87,0.3); padding: 6px 26px 6px 10px; appearance: none; cursor: pointer; outline: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23B08D57'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 8px center; }
        .cd-cart-pill { display: flex; align-items: center; gap: 6px; font-size: 9px; font-weight: 600; letter-spacing: 0.16em; text-transform: uppercase; color: #FAF8F4; background: #1C1A18; padding: 8px 14px; border: none; cursor: pointer; transition: background 0.2s; }
        .cd-cart-pill:hover { background: #B08D57; }
        .cd-cart-count { background: #B08D57; color: #FAF8F4; border-radius: 50%; width: 17px; height: 17px; display: flex; align-items: center; justify-content: center; font-size: 8px; font-weight: 700; }
        .cd-grid-wrap { position: relative; z-index: 1; max-width: 1280px; margin: 0 auto; padding: 28px clamp(20px,5vw,56px) 80px; }
        .cd-grid { display: grid; grid-template-columns: repeat(4,1fr); gap: 18px; }
        .cd-card { background: #fff; border: 1px solid rgba(176,141,87,0.1); border-radius: 2px; overflow: hidden; transition: box-shadow 0.3s, border-color 0.3s; }
        .cd-card:hover { box-shadow: 0 10px 36px rgba(28,26,24,0.09); border-color: rgba(176,141,87,0.28); }
        .cd-img-wrap { position: relative; overflow: hidden; aspect-ratio: 1/1; background: #F0EAE0; }
        .cd-slide-wrap { width: 100%; height: 100%; position: relative; overflow: hidden; }
        .cd-slide-strip { display: flex; width: 100%; height: 100%; transition: transform 0.35s ease; }
        .cd-slide-strip img { flex: 0 0 100%; width: 100%; height: 100%; object-fit: cover; object-position: center; display: block; }
        .cd-sarrow { position: absolute; top: 50%; transform: translateY(-50%); width: 30px; height: 30px; border: none; border-radius: 50%; background: rgba(255,255,255,0.9); color: #1C1A18; cursor: pointer; display: flex; align-items: center; justify-content: center; z-index: 4; opacity: 0; transition: opacity 0.2s, background 0.2s; }
        .cd-img-wrap:hover .cd-sarrow,
        .cd-card:hover .cd-sarrow { opacity: 1; }
        .cd-sarrow:hover { background: #fff; }
        .cd-sarrow-l { left: 10px; }
        .cd-sarrow-r { right: 10px; }
        .cd-img-wrap img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform 0.6s cubic-bezier(0.25,0.46,0.45,0.94); }
        .cd-card:hover .cd-img-wrap img { transform: scale(1.06); }
        .cd-wish-btn { position: absolute; top: 9px; right: 9px; z-index: 3; width: 30px; height: 30px; background: rgba(250,248,244,0.9); border: none; border-radius: 50%; display: flex; align-items: center; justify-content: center; cursor: pointer; }
        .cd-badge { position: absolute; top: 9px; left: 9px; font-size: 7.5px; font-weight: 700; letter-spacing: 0.16em; text-transform: uppercase; padding: 4px 8px; z-index: 3; border-radius: 1px; }
        .cd-info { padding: 12px 13px 14px; }
        .cd-name { font-family: 'Jost', sans-serif; font-size: 13px; font-weight: 500; color: #1C1A18; letter-spacing: 0.01em; line-height: 1.35; display: block; margin-bottom: 7px; transition: color 0.2s; }
        .cd-name:hover { color: #B08D57; }
        .cd-price-row { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; margin-bottom: 10px; }
        .cd-price { font-family: 'Jost', sans-serif; font-size: 15px; font-weight: 600; color: #B08D57; letter-spacing: 0.04em; }
        .cd-mrp { font-size: 11px; color: #AEA39A; text-decoration: line-through; font-weight: 300; }
        .cd-discount { font-size: 8px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; color: #C0392B; background: rgba(192,57,43,0.08); padding: 2px 5px; }
        .cd-atc { width: 100%; background: #1C1A18; color: #FAF8F4; border: none; font-family: 'Jost', sans-serif; font-size: 9px; font-weight: 600; letter-spacing: 0.2em; text-transform: uppercase; padding: 10px 0; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 7px; position: relative; overflow: hidden; transition: color 0.28s; }
        .cd-atc::after { content: ''; position: absolute; inset: 0; background: #B08D57; transform: translateX(-101%); transition: transform 0.28s cubic-bezier(0.4,0,0.2,1); z-index: 0; }
        .cd-atc:hover::after { transform: translateX(0); }
        .cd-atc > * { position: relative; z-index: 1; }
        .cd-toast { position: fixed; bottom: 40px; left: 20px; right: 20px; width: auto; background: #1C1A18; color: #FAF8F4; font-family: 'Jost', sans-serif; font-size: 11px; font-weight: 500; letter-spacing: 0.1em; padding: 12px 22px; display: flex; align-items: center; justify-content: center; gap: 8px; z-index: 9999; border-radius: 1px; box-shadow: 0 8px 32px rgba(0,0,0,0.22); white-space: nowrap; text-align: center; }
        .cd-loading { text-align: center; padding: 80px; font-size: 13px; color: #9A9186; letter-spacing: 0.1em; }
        .cd-error { text-align: center; padding: 40px; color: #C0392B; font-size: 13px; }
        .cd-empty { text-align: center; padding: 80px 20px; color: #9A9186; }
        .cd-empty-title { font-family: 'Playfair Display', serif; font-size: 22px; color: #3a3530; margin-bottom: 8px; }
        .cd-ornament { display: flex; align-items: center; justify-content: center; gap: 16px; margin-top: 52px; opacity: 0.35; }
        .cd-ornament::before { content:''; width:80px; height:1px; background: linear-gradient(to right, transparent, #B08D57); }
        .cd-ornament::after  { content:''; width:80px; height:1px; background: linear-gradient(to left,  transparent, #B08D57); }
        .cd-diamond { width:6px; height:6px; background:#B08D57; transform:rotate(45deg); }
        @media (max-width: 1024px) { .cd-grid { grid-template-columns: repeat(3,1fr); } }
        @media (max-width: 700px) { .cd-grid { grid-template-columns: repeat(2,1fr); gap:10px; } }
        @media (max-width: 400px) { .cd-grid { gap: 8px; } }
        @media (hover: none) {
        .cd-atc::after { display: none; }
        .cd-atc:active { background: #B08D57; transition: background 0.1s; }
      }
      `}</style>

      <div className="cd-page">
        {/* Hero */}
        {/* Header */}
      <div className="cd-header">
        <Link to="/collections" className="cd-header-back">
          <ArrowLeft size={11} /> All Collections
        </Link>
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55 }}>
          <p className="cd-header-eyebrow">{meta?.sub || 'Collection'}</p>
          <h1 className="cd-header-title">{meta?.name || ''}</h1>
        </motion.div>
        <div className="cd-header-rule" />
      </div>

        {/* Toolbar */}
        <div className="cd-toolbar">
          <p className="cd-toolbar-left">Showing <span>{sorted.length} products</span></p>
          <div style={{ display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap' }}>
            <div className="cd-sort">
              <span>Sort:</span>
              <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
                <option value="default">Featured</option>
                <option value="price-asc">Price: Low to High</option>
                <option value="price-desc">Price: High to Low</option>
                <option value="discount">Best Discount</option>
              </select>
            </div>
            
          </div>
        </div>

        {/* Products */}
        <div className="cd-grid-wrap">
          {loading ? (
            <div className="cd-loading">Loading products…</div>
          ) : error ? (
            <div className="cd-error">{error}</div>
          ) : sorted.length === 0 ? (
            <div className="cd-empty">
              <div className="cd-empty-title">No products yet</div>
              <p>This collection is coming soon.</p>
            </div>
          ) : (
            <div className="cd-grid">
              {sorted.map((p, i) => (
                <ProductCard
                  key={p.id} product={p} index={i}
                  onAddToCart={handleAddToCart}
                  collectionName={meta?.name || ''}
                  collectionSlug={slug}
                  wished={wishlist.has(p.id)}
                  onToggleWishlist={() => handleWishlist(p.id)}
                />
              ))}
            </div>
          )}

          <motion.div className="cd-ornament"
            initial={{ opacity: 0 }} whileInView={{ opacity: 0.35 }}
            viewport={{ once: true }} transition={{ duration: 0.8 }}>
            <div className="cd-diamond" />
          </motion.div>
        </div>
      </div>

      <Toast msg={toast.msg} show={toast.show} />
    </>
  );
};

export default CollectionDetail;